import { watchDebounced } from '@vueuse/core'
import { joinURL } from 'ufo'
import type { Ref } from 'vue'

import type { GetSearchUrl } from '../../../utils/host-browser-apis/getSearchUrl'

import type { SearchSuggestionOptions } from '../../../server/api/v2/typeahead/index.types'

type SimpleSuggestion = {
  text: string
  icon?: 'search' | 'external-link'
  action?: 'search' | 'navigate-url'
}
type RichSuggestion = SimpleSuggestion & {
  image: string
  description: string
}

export type Suggestion = SimpleSuggestion | RichSuggestion
export type SearchSuggestions = Suggestion[]

function yahooSearchSuggestionsToSearchSuggestionsAdapter(
  suggestions: GetSearchSuggestionsFromYahooApi
): SearchSuggestions {
  const [, textSuggestions, , , suggestionMetadata] = suggestions

  return textSuggestions.map((text: string, index: number) => ({
    id: Math.random().toString(),
    text,
    image:
      suggestionMetadata?.['google:suggestdetail']?.[index]?.i ?? undefined,
    description:
      suggestionMetadata?.['google:suggestdetail']?.[index]?.a ?? undefined,
  }))
}

interface UseSearchSuggestions {
  query: Ref<string | undefined>
  suggestions: Ref<SearchSuggestions>
  openSearchSuggestionLink: (selectedSuggestion: Suggestion) => Promise<void>
  openSearchLink: (
    search: string,
    event: 'keyboard-return' | 'search-button'
  ) => Promise<void>
}

export type UseSearchSuggestionOptions = SearchSuggestionOptions & {
  /** Debounce time in milliseconds */
  debounce?: number
}

async function logQueryEvent(
  trigger: 'suggestion-press' | 'keyboard-return' | 'search-button',
  { isMonetized }: { isMonetized: boolean | undefined }
) {
  const { track } = useAnalytics()

  const isMonetizationApiAvailable = isMonetized !== undefined
  if (isMonetizationApiAvailable) {
    const activeSearchProvider = await useActiveSearchProvider()
    if (isMonetized) {
      track('query', {
        location: 'ntp',
        monetized: true,
        trigger,
        active_search_provider: activeSearchProvider,
      })
    }

    if (!isMonetized) {
      track('QueryNonMonetized_Submitted', {
        location: 'ntp',
        monetized: false,
        trigger,
        active_search_provider: activeSearchProvider,
      })
    }
  }

  if (!isMonetizationApiAvailable) {
    track('query', { location: 'ntp', trigger })
  }
}

function buildLinkForQuery(query: string): Promise<GetSearchUrl> {
  return HostBrowserApis.getSearchUrl(query, {
    fallbackUrl: 'https://search.yahoo.com/yhs/search?p={searchTerms}',
    queryPlaceholder: '{searchTerms}',
  })
}

export const useSearchSuggestions = (
  options?: UseSearchSuggestionOptions
): UseSearchSuggestions => {
  const query = ref<string>()
  const suggestions = ref<SearchSuggestions>([])
  const abortController = ref<AbortController>()

  function cleanSearch() {
    suggestions.value = []
    query.value = ''
  }

  watchDebounced(
    query,
    async (newQuery) => {
      if (!newQuery) {
        suggestions.value = []
        return
      }

      if (abortController.value) {
        abortController.value?.abort()
      }

      abortController.value = new AbortController()
      const signal = abortController.value.signal

      // Prefer the Shift extension API when the browser exposes it; otherwise
      // fall through to the /api/v2/typeahead proxy below.
      const extensionSuggestions =
        await HostBrowserApis.getSearchSuggestions(newQuery)

      // A newer query aborts this controller's signal; drop the stale result.
      if (signal.aborted) return

      if (extensionSuggestions !== null) {
        suggestions.value =
          options?.suggestionType === 'simple'
            ? extensionSuggestions.map((s) => ({
                ...s,
                image: undefined,
                description: undefined,
              }))
            : extensionSuggestions
        return
      }

      const url = joinURL('/api/v2/typeahead/', newQuery)
      try {
        const rawSuggestion = await $fetch<GetSearchSuggestionsFromYahooApi>(
          url,
          {
            params: options,
            timeout: 5000,
            retry: 0,
            signal,
            ignoreResponseError: true,
          }
        )

        if (!rawSuggestion || rawSuggestion.length < 1) return
        suggestions.value =
          yahooSearchSuggestionsToSearchSuggestionsAdapter(rawSuggestion)
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Handling abort errors to avoid showing errors in the console
        }
      }
    },
    { debounce: options?.debounce }
  )

  const openSearchSuggestionLink = async (selectedSuggestion: Suggestion) => {
    const { url, isMonetized } = await buildLinkForQuery(
      selectedSuggestion.text
    )

    cleanSearch()

    await logQueryEvent('suggestion-press', { isMonetized })

    window.open(url, '_blank')
  }

  const openSearchLink = async (
    search: string,
    event: 'keyboard-return' | 'search-button'
  ) => {
    const { url, isMonetized } = await buildLinkForQuery(search)

    cleanSearch()

    await logQueryEvent(event, { isMonetized })

    window.open(url, '_blank')
  }

  return {
    query,
    suggestions,
    openSearchSuggestionLink,
    openSearchLink,
  }
}
